import React, { ReactInstance, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery } from 'react-apollo'
import MediaQuery from 'react-responsive'
import { useHistory } from 'react-router-dom'
import { Col, Row } from 'react-styled-flexboxgrid'

import { AcornsIcon } from '@acorns/icons'
import {
  ActionStrip,
  ActionStripAction,
  BaseModal,
  Button,
  ConfirmationModal,
  Ink,
  Item,
  Menu,
  ModalAction,
  ModalActionTypes,
  ModalProps,
  Overlay,
  ProgressLoader,
  Spinner,
  WebViewActionStrip,
} from '@acorns/web-components'
import {
  EnvContextType,
  useAnalyticsClient,
  useRemoteStrings,
} from '@acorns/web-utils'
import { format, parseISO } from 'date-fns'
import noop from 'lodash-es/noop'
import styled, { ThemeProvider } from 'styled-components'

import {
  InvestmentAccount,
  InvestmentAccountStatus,
  RecurringInvestmentSettings,
  Tier,
  TierKey,
  TierPrice,
  TierProduct,
} from 'generated/types'
import { useScrollIntoView } from 'hooks/useScrollIntoView'
import useWindowDimensions from 'hooks/useWindowDimensions'
import themeValues, { style } from 'theme'

import EdgeCasePage from './close-account-edge-cases'
import CustomFullscreenTakeover from './custom-fullscreen-takeover'
import customTheme from './customThemeConfig'
import stopRecurringInvestmentMutation from './mutations/stop-recurring-investment.mutation.gql'
import cancelRecurringMutation from './mutations/stop-recurring-transfer.mutation.gql'
import subscribeUserToTier from './mutations/subscribe-to-tier.mutation.gql'
import nextBillingDateQuery from './queries/next-billing-date.query.gql'
import fetchTierGroup from './queries/tier-group-by-user.gql'
import fetchEarlyTierGroup from './queries/tier-group-early-tier.gql'
import subscriptionTierQuery from './queries/user-tier-query.gql'
import {
  CancelSubscriptionTapped,
  CloseAccountButtonTapped,
  CloseAccountScreenViewed,
  CloseEarlyTapped,
  CloseLaterTapped,
  CloseSpendTapped,
  DowngradeModalCancelCTA,
  DowngradeModalConfirmCTA,
  DowngradeModalViewed,
  PassionsModalConfirmCTA,
  PassionsModalViewed,
  PendingInvestmentsTurnOffRecurring,
  UpgradeSubscriptionCancelCTA,
  UpgradeSubscriptionConfirmCTA,
  UpgradeSubscriptionConfirmationModal,
} from './segment'
import TierCard from './tier-card'
import {
  EligibilityStatus,
  determineAccountClosureEligibility,
  useEdgeCaseProductInfo,
} from './utils'

export interface TierProps {
  cost: string
  product: string
  currentPlan: boolean
  tier: Tier
  active?: boolean
  slogans: string[]
  setCurrentTierShowing: (value: boolean) => void
  ref: React.MutableRefObject<ReactInstance>
}

type StateProps = {
  showModal: boolean
  setShowModal: (showModal: boolean) => void
  showEdgeCaseView: boolean
  setShowEdgeCaseView: (showEdgeCaseView: boolean) => void
  progressAmount: number
  setProgressAmount: (progressAmount: number) => true
  animationTiming: number
  setAnimationTiming: (animationTiming: number) => true
  shouldShowConfirmUpgradeModal: boolean
  setShouldShowConfirmUpgradeModal: (
    shouldShowConfirmUpgradeModal: boolean,
  ) => void
  showRecurringTransferModal: boolean
  setShowRecurringTransferModal: (showRecurringTransferModal: boolean) => void
  shouldShowLoadingSpinner: boolean
  setShouldShowLoadingSpinner: (shouldShowLoadingSpinner: boolean) => void
  showEducationModal: boolean
  setShowEducationModal: (showEducationModal: boolean) => void
  selectedTierForChange: {
    key: string
    id: string
    isDowngrade: boolean
  } | null
  setSelectedTierForChange: (selectedTierForChange: {
    key: string
    id: string
    isDowngrade: boolean
  }) => true
}

export interface UserProps {
  currentTier: {
    id: string
    key: string
    includedProducts: TierProduct[]
  }
}

export type AccountDetails = {
  id: string
  status: InvestmentAccountStatus
  balance: number
  pendingBalance: number
  recurringInvestmentSettings: RecurringInvestmentSettings
}

export type EarlyAccountDetails = {
  id: string
  status: InvestmentAccountStatus
  balance: number
  pendingBalance: number
  beneficiaryId: string
  recurringInvestmentSettings: RecurringInvestmentSettings
}
export type AccountState = {
  hasFundingSource: boolean
  isVerifiedUser: boolean
  hasVerifiedFS: boolean
  investAccount: AccountDetails[]
  laterAccounts: AccountDetails[]
  earlyAccounts: EarlyAccountDetails[]
}
export type Props = {
  accountTryingToCancel: string
  product: string
  exitWebViewCancel: () => void
  accountsMenu: []
  isWebView: boolean
  requiresSupportToClose: boolean
  selectedTier: boolean
  tiers: Tier[]
  tierCardData: [
    {
      tier: Tier
      tierPrices: TierPrice[]
      preferredTierPrice: TierPrice
    },
  ]
  nextBillingDate: string
  pendingVerificationModalProps: Modal
  nonVerifiedFSModalProps: Modal
  educationConfirmationModalProps: Modal
  userAccountState: AccountState
  backOverride: string
  edgeCaseErrorData: {
    eligible: boolean
    status: string
    tier?: string
    recurringOn?: boolean
    spendRecurringTransferOn?: boolean
    spendTransferId?: string
    laterRecurringInvestment?: boolean
    investRecurringInvestmentOn?: boolean
  }
  confirmationModalProps: ModalProps[]
  shouldShowHistory: boolean
}

type Handler = {
  handleCancelProductPress: (product: string) => void
  handleCancelSubscriptionPress: () => void
  handleCloseModal: () => void
  handleActionClick: () => void
  handleUpgradeSubscriptionPress: (tierId: string) => void
  handleAccountsMenuItemPress: (product: string) => void
  handleMobileAccountsMenuItemPress: (product: string) => void
  handleBackPress: () => void
  handleRecurringTransferCancellation: () => void
  handleCloseRecurringModal: () => void
  handleStartCancelFlow: () => void
  handleCloseEducationModal: () => void
  handleCloseFSVerificationModal: () => void
  handleSubmitVerifyFSModal: () => void
  handleDowngradeTierSelection: (tierId: string, tierName: string) => void
  handleUpgradeTierSelection: (tierId: string, tierKey: string) => void
}

type Modal = {
  modalHeader: string
  modalBody: string
  modalImage?: string
  modalActions: ModalAction[]
}

type MenuStateProps = {
  showCloseAccountsDropdown: boolean
  setShowCloseAccountsDropdown: (showCloseAccountsDropdown: boolean) => void
}

type MenuHandler = {
  handleClickOutside: () => void
}

type MenuProps = MenuStateProps & MenuHandler
export type MergedProps = Props &
  StateProps &
  MenuProps &
  Handler &
  EnvContextType &
  UserProps

const StyledFullscreenTakeover = styled(CustomFullscreenTakeover)<{
  innerHeight: number
}>`
  background: ${style((theme) => theme.colors.white)};
  padding-bottom: 75px;
`
const TierContainer = styled<any>(Row)`
  position: relative;
  margin: 0 auto;
  max-width: 1200px;
  height: auto;
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    width: 100%;
    ${(props) => (props.isWebView ? 'padding: 0 20px' : 'padding: 0')};
  }
`
const StyledCancelButton = styled(Button)`
  border-radius: 100px;
  border: 1px solid ${style((theme) => theme.colors.stoneLight)};
  height: 44px;
  width: 100%;
  margin: 30px auto;
`

const StyledCancelButtonWV = styled(StyledCancelButton)`
  margin-bottom: 20px;
`

const ActionStripWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`

const StyledCol = styled(Col)`
  @media screen and (min-width: 890px) {
    :first-child {
      padding-left: 0px;
    }
    :last-child {
      padding-right: 0px;
    }
  }
  @media screen and (max-width: 889px) {
    padding-left: 0px;
    padding-right: 0px;
  }
`
const Container = styled.div`
  margin: 0 auto;
  @media screen and (max-width: 890px) and (min-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    margin: 0 40px;
  }
`
const WVContainer = styled(Container)`
  background-color: ${style((theme) => theme.colors.white)};
`

const StyledIcon = styled.svg`
  height: 24px;
  width: 24px;
  object-fit: contain;
  vertical-align: middle;
`

const StyledText = styled.span`
  height: 22px;
  line-height: 22px;
  margin: 0 auto 0 10px;
`

const StyledOverlay = styled(Overlay)`
  &&& {
    top: 0;
  }
`

const StyledMenu = styled(Menu)`
  &&& {
    background-color: #fff;
    border-radius: ${style((theme) => theme.styles.borderRadius)};
    box-shadow: ${style((theme) => theme.shadows.lgMedium)};
    &::before {
      right: 80px;
    }
    @media screen and (min-width: ${style((theme) => theme.breakpoints.md)}) {
      margin-right: 30px;
      right: 30px;
      top: 94px;
    }
  }
`

const Loader = styled.div`
  align-items: center;
  display: flex;
  height: 100vh;
`

const webviewExit = (mobilePath: string) => {
  window.location.assign(mobilePath)
}

const TieredRegSubscriptionCenter = () => {
  const { getRemoteString, StyledRemoteString } = useRemoteStrings()
  const [edgeCaseErrorData, setEdgeCaseErrorData] =
    useState<Partial<EligibilityStatus> | null>(null)
  const [showEdgeCaseView, setShowEdgeCaseView] = useState(false)
  const [showEducationModal, setShowEducationModal] = useState(false)
  const [accountTryingToCancel, setAccountTryingToCancel] = useState<
    TierProps['product'] | null
  >(null)
  const [showCloseAccountsDropdown, setShowCloseAccountsDropdown] =
    useState(false)
  const [shouldShowNonVerifiedFSModal, setShouldShowNonVerifiedFSModal] =
    useState(false)
  const [showRecurringTransferModal, setShowRecurringTransferModal] =
    useState(false)
  const [shouldShowConfirmUpgradeModal, setShouldShowConfirmUpgradeModal] =
    useState(false)
  const [shouldShowConfirmDowngradeModal, setShouldShowConfirmDowngradeModal] =
    useState(false)
  const [selectedTierForChange, setSelectedTierForChange] =
    useState<MergedProps['selectedTierForChange']>(null)
  const [shouldShowLoadingSpinner, setShouldShowLoadingSpinner] =
    useState(false)
  const [showSpendUnavailableModal, setShowSpendUnavailableModal] =
    useState(false)
  const [progressAmount, setProgressAmount] = useState(0)
  const [animationTiming, setAnimationTiming] = useState(2)
  const [currentTierShowing, setCurrentTierShowing] = useState(false)
  const [
    shouldShowEarlyNotSupportedModal,
    setShouldShowEarlyNotSupportedModal,
  ] = useState(false)
  const [
    shouldShowPassionsNotSupportedModal,
    setShouldShowPassionsNotSupportedModal,
  ] = useState(false)
  const [nextBillingDate, setNextBillingDate] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [startCancelFlow, setStartCancelFlow] = useState(false)

  useQuery(nextBillingDateQuery, {
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
    onCompleted: ({ data }) => {
      const invoiceDate = data?.nextBillingDate?.next_billing_date
      if (invoiceDate) {
        setNextBillingDate(format(parseISO(invoiceDate), 'MM/dd/yyyy'))
      } else {
        setNextBillingDate(null)
      }
    },
  })
  const { data: earlyTierGroupData } = useQuery(fetchEarlyTierGroup)
  const { data: tierData } = useQuery(subscriptionTierQuery, {
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  })

  const [stopRecurringInvestmentRequest] = useMutation(
    stopRecurringInvestmentMutation,
  )
  const [cancelRecurringTransfers] = useMutation(cancelRecurringMutation)
  const [subscribeUserToTierMutation] = useMutation(subscribeUserToTier)

  const history = useHistory()
  const analytics = useAnalyticsClient()
  const userAccountState: any = useEdgeCaseProductInfo()

  useEffect(() => {
    if (userAccountState && tierData) {
      setLoading(false)
    }
  }, [userAccountState])

  const { height } = useWindowDimensions()

  const currentTierRef = useRef<HTMLInputElement>()

  const isWebView = window.location.pathname.startsWith('/wv/')

  const hasLaterAccount = !!userAccountState?.laterAccount
  const hasSpendAccount = userAccountState?.spendAccount?.active

  const hasClosedSpendAccount =
    userAccountState && userAccountState?.spendAccount?.isClosed

  const hasEarlyAccount = userAccountState?.earlyAccounts?.length > 0
  const isInvestOnly = !hasLaterAccount && !hasSpendAccount && !hasEarlyAccount
  let currentTier: UserProps['currentTier']

  let tierCardData: Props['tierCardData'] | null = null

  if (!loading) {
    analytics.track('Screen Viewed', CloseAccountScreenViewed)

    currentTier = tierData?.tierSubscriptionForUser?.tier
    tierCardData = earlyTierGroupData?.tierGroup?.tierOptions
  }

  const cancelOptions = []
  if (hasLaterAccount) {
    cancelOptions.push({
      option: 'Close Later account',
      icon: <AcornsIcon icon={AcornsIcon.Icon.Later} width="24px" />,
      product: 'later',
    })
  }
  if (hasSpendAccount) {
    cancelOptions.push({
      option: 'Close Checking account',
      icon: <AcornsIcon icon={AcornsIcon.Icon.Card} width="24px" />,
      product: 'spend',
    })
  }
  if (hasEarlyAccount) {
    cancelOptions.push({
      option: 'Close Early account',
      icon: <AcornsIcon icon={AcornsIcon.Icon.ProductEarly} width="24px" />,
      product: 'early',
    })
  }

  cancelOptions.push({
    option: 'Cancel subscription',
    icon: <AcornsIcon icon={AcornsIcon.Icon.Cancel} width="24px" />,
    product: 'subscription',
  })

  const accountsMenu = cancelOptions

  const handleCancelDowngrade = () => {
    analytics.track('Button Tapped', DowngradeModalCancelCTA)
    setShouldShowConfirmDowngradeModal(false)
  }

  const handleCancelUpgrade = () => {
    analytics.track('Button Tapped', UpgradeSubscriptionCancelCTA)
    shouldShowConfirmUpgradeModal
      ? setShouldShowConfirmUpgradeModal(false)
      : setShowSpendUnavailableModal(false)
  }

  const handleDowngradeTierSelection = (tierId: string, tierKey: string) => {
    analytics.track('Container Viewed', DowngradeModalViewed)
    setSelectedTierForChange({
      key: tierKey,
      id: tierId,
      isDowngrade: true,
    })
    if (userAccountState?.earlyAccounts?.length > 0) {
      setShouldShowEarlyNotSupportedModal(true)
    } else if (userAccountState?.hasPassions) {
      setShouldShowPassionsNotSupportedModal(true)
    } else {
      setShouldShowConfirmDowngradeModal(true)
    }
  }

  const handleUpgradeTierSelection = (tierId: string, tierKey: string) => {
    analytics.track(
      'Container Viewed',
      UpgradeSubscriptionConfirmationModal(currentTier?.key),
    )

    setSelectedTierForChange({
      key: tierKey,
      id: tierId,
      isDowngrade: false,
    })
    hasClosedSpendAccount &&
    currentTier?.key !== TierKey.Silver &&
    !userAccountState?.spendAccount?.isReopenable
      ? setShowSpendUnavailableModal(true)
      : setShouldShowConfirmUpgradeModal(true)
  }

  const handleDowngradeSubscriptionPress = () => {
    analytics.track('Button Tapped', DowngradeModalConfirmCTA)

    sessionStorage.setItem(
      'requestedTierDowngrade',
      selectedTierForChange?.key ?? '',
    )

    const url = isWebView
      ? '/wv/my-subscription/downgrade'
      : '/settings/my-subscription/downgrade'

    setShouldShowConfirmDowngradeModal(false)
    setShouldShowLoadingSpinner(true)
    setAnimationTiming(1)
    setTimeout(() => {
      setProgressAmount(60)
    }, 250)

    subscribeUserToTierMutation({
      variables: { tierPriceId: selectedTierForChange?.id },
      refetchQueries: [
        { query: subscriptionTierQuery },
        { query: fetchTierGroup },
      ],
    })
      .then((response) => {
        if (response.data?.subscribeUserToTier?.actions?.length > 0) {
          setAnimationTiming(1)
          setProgressAmount(80)
          const closureEligibility = determineAccountClosureEligibility(
            userAccountState,
            selectedTierForChange?.key ?? '',
            currentTier,
          )

          if (closureEligibility.eligible) {
            setAnimationTiming(1)
            setProgressAmount(100)
            setShouldShowLoadingSpinner(false)

            setTimeout(() => {
              history.push(url, {
                referrer: location.pathname,
                productsToClose: response.data.subscribeUserToTier.actions,
                hasLaterBalance: userAccountState?.laterAccount?.balance > 0,
              })
            }, 250)
          } else {
            setAnimationTiming(1)
            setProgressAmount(100)
            setEdgeCaseErrorData(closureEligibility)
            if (closureEligibility.status === 'nonverified fs') {
              setShouldShowLoadingSpinner(false)
              setShouldShowNonVerifiedFSModal(true)
            } else {
              setShouldShowLoadingSpinner(false)
              setTimeout(() => {
                setShowEdgeCaseView(true)
              }, 1000)
            }
            setProgressAmount(20)
          }
        } else {
          setAnimationTiming(1)
          setProgressAmount(100)
          setTimeout(() => {
            setShouldShowLoadingSpinner(false)
          }, 250)
          setSelectedTierForChange(null)
        }
      })
      .catch((error) => {
        setShouldShowLoadingSpinner(false)
        throw error
      })
  }

  const handleUpgradeSubscriptionPress = () => {
    analytics.track(
      'Button Tapped',
      UpgradeSubscriptionConfirmCTA(
        currentTier?.key,
        selectedTierForChange?.key,
      ),
    )
    setAnimationTiming(1)
    setProgressAmount(15)
    const url = isWebView ? '/wv/upgrade-success' : '/upgrade-success'
    setShouldShowConfirmUpgradeModal(false)
    setShouldShowLoadingSpinner(true)

    setTimeout(() => {
      setProgressAmount(65)
    }, 250)

    subscribeUserToTierMutation({
      variables: { tierPriceId: selectedTierForChange?.id },
      refetchQueries: [{ query: subscriptionTierQuery }],
    })
      .then(() => {
        setAnimationTiming(1)
        setProgressAmount(100)
        setShouldShowLoadingSpinner(false)
      })
      .then(() =>
        setTimeout(() => {
          history.push(url, { referrer: location.pathname })
        }, 250),
      )
      .catch((error) => {
        setShouldShowLoadingSpinner(false)
        throw error
      })
  }

  const handleBackPress = () => {
    setShowEdgeCaseView(false)
  }

  const handleRecurringTransferCancellation = () => {
    analytics.track('Button Tapped', PendingInvestmentsTurnOffRecurring)
    if (selectedTierForChange?.key === TierKey.Silver) {
      if (
        userAccountState?.earlyAcounts?.find(
          (account: InvestmentAccount) =>
            account?.recurringInvestmentSettings?.amount,
        )
      ) {
        userAccountState?.earlyAccounts.forEach(
          (account: InvestmentAccount) => {
            if (account?.recurringInvestmentSettings?.amount) {
              stopRecurringInvestmentRequest({
                variables: {
                  investmentAccountId: account.id,
                },
              })
            }
          },
        )
      }
    } else if (selectedTierForChange?.key === TierKey.Bronze) {
      if (
        userAccountState?.earlyAcounts?.find(
          (account: InvestmentAccount) =>
            account?.recurringInvestmentSettings?.amount,
        )
      ) {
        userAccountState?.earlyAccounts.forEach(
          (account: InvestmentAccount) => {
            if (account?.recurringInvestmentSettings?.amount) {
              stopRecurringInvestmentRequest({
                variables: {
                  investmentAccountId: account.id,
                },
              })
            }
          },
        )
      }
      if (userAccountState?.laterAccount?.recurringInvestmentSettings?.amount) {
        stopRecurringInvestmentRequest({
          variables: {
            investmentAccountId: userAccountState?.laterAccount?.id,
          },
        })
      }

      if (userAccountState?.spendAccount?.hasRecurringTransferOn) {
        cancelRecurringTransfers({
          variables: {
            id: userAccountState?.spendAccount?.recurringTransferId,
          },
        })
      }
    }
    if (accountTryingToCancel === 'subscription') {
      if (
        userAccountState?.earlyAcounts?.find(
          (account: InvestmentAccount) =>
            account?.recurringInvestmentSettings?.amount,
        )
      ) {
        userAccountState?.earlyAccounts.forEach(
          (account: InvestmentAccount) => {
            if (account?.recurringInvestmentSettings?.amount) {
              stopRecurringInvestmentRequest({
                variables: {
                  investmentAccountId: account.id,
                },
              })
            }
          },
        )
      }

      if (userAccountState?.laterAccount?.recurringInvestmentSettings?.amount) {
        stopRecurringInvestmentRequest({
          variables: {
            investmentAccountId: userAccountState?.laterAccount.id,
          },
        })
      }

      if (userAccountState?.spendAccount?.hasRecurringTransferOn) {
        cancelRecurringTransfers({
          variables: {
            id: userAccountState?.spendAccount?.recurringTransferId,
          },
        })
      }

      if (
        userAccountState?.investAccount?.recurringInvestmentSettings?.amount
      ) {
        stopRecurringInvestmentRequest({
          variables: {
            investmentAccountId: userAccountState?.investAccount?.id,
          },
        })
      }
      setShowRecurringTransferModal(true)
    } else if (accountTryingToCancel === 'later') {
      stopRecurringInvestmentRequest({
        variables: {
          investmentAccountId: userAccountState?.laterAccount?.id,
        },
      }).then(() => {
        setShowRecurringTransferModal(true)
      })
    } else if (accountTryingToCancel === 'spend') {
      if (userAccountState?.spendAccount?.hasRecurringTransferOn) {
        cancelRecurringTransfers({
          variables: {
            id: userAccountState?.spendAccount?.recurringTransferId,
          },
        }).then(() => {
          setShowRecurringTransferModal(true)
        })
      }
    } else {
      stopRecurringInvestmentRequest({
        variables: {
          investmentAccountId: userAccountState?.investAccount?.id,
        },
      }).then(() => {
        setShowRecurringTransferModal(true)
      })
    }
  }

  const exitWebViewCancel = () => webviewExit('acorns://exit?navigate_to=back')

  const handleWVNavigateHistory = () => {
    history.push('/wv/subscription-history', {
      referrer: location.pathname,
    })
  }

  const handleEarlyModalCancel = () => {
    setShouldShowEarlyNotSupportedModal(false)
    userAccountState?.hasPassions &&
      setShouldShowPassionsNotSupportedModal(true)
  }
  const routeToEarly = () => {
    const url = isWebView ? '/wv/close-account/early' : '/close-account/early'
    history.push(url, {
      referrer: location.pathname,
    })
  }

  const handleStartCancelFlow = () => {
    if (accountTryingToCancel === 'subscription') {
      history.push(
        isWebView ? '/wv/cancel-subscription' : '/cancel-subscription',
        {
          referrer: location.pathname,
          hideFooter: true,
          hasLaterAccount: userAccountState?.laterAccount?.id,
          hasSpendAccount: userAccountState?.spendAccount?.active,
          isCancelSubscriptionFlow: true,
          hasLaterBalance: userAccountState?.laterAccount?.balance > 0,
        },
      )
    } else {
      history.push(
        isWebView
          ? `/wv/close-account/${accountTryingToCancel}/`
          : `/close-account/${accountTryingToCancel}`,
        {
          referrer: location.pathname,
          hideFooter: true,
        },
      )
    }
  }

  const handleSubmitVerifyFSModal = () => {
    isWebView
      ? webviewExit(
          'acorns://event?flow_complete=false&exit_reason=rdv-required',
        )
      : history.push('/settings/linked-accounts', {
          referrer: '/settings/my-subscription',
        })
  }

  const handleCloseFSVerificationModal = () => {
    setShouldShowNonVerifiedFSModal(false)
  }

  const handleCloseEducationModal = () => {
    setShowEducationModal(false)
  }

  const handleTrackAnalytics = (product: TierProps['product']) => {
    let trackedEvent
    switch (product) {
      case 'early':
        trackedEvent = CloseEarlyTapped
        break
      case 'spend':
        trackedEvent = CloseSpendTapped
        break
      case 'later':
        trackedEvent = CloseLaterTapped
        break
      case 'subscription':
        trackedEvent = CancelSubscriptionTapped
        break
    }
    analytics.track('Button Tapped', CloseAccountButtonTapped)
    analytics.track('Button Tapped', {
      ...trackedEvent,
    })
  }

  const handleAccountsMenuItemPress = (product: TierProps['product']) => {
    handleTrackAnalytics(product)
    setShowCloseAccountsDropdown(false)
    const closureEligibility = determineAccountClosureEligibility(
      userAccountState as AccountState,
      product,
      currentTier,
    )
    if (closureEligibility.eligible) {
      if (
        product === 'subscription' &&
        userAccountState?.earlyAccounts?.length > 0
      ) {
        setShouldShowEarlyNotSupportedModal(true)
      } else if (product === 'subscription') {
        setShowEducationModal(true)
      } else {
        setAccountTryingToCancel(product)
        setStartCancelFlow(true)
      }
    } else {
      setEdgeCaseErrorData(closureEligibility)

      if (closureEligibility.status === 'nonverified fs') {
        setShouldShowNonVerifiedFSModal(true)
      } else {
        setShowEdgeCaseView(true)
      }
    }
    setAccountTryingToCancel(product)
  }

  const handleActionClick = () => {
    !isInvestOnly
      ? setShowCloseAccountsDropdown(!showCloseAccountsDropdown)
      : handleAccountsMenuItemPress('subscription')
  }

  const handleClickOutside = () => {
    if (showCloseAccountsDropdown) {
      setShowCloseAccountsDropdown(false)
    }
  }

  const StyledContainer = isWebView ? WVContainer : Container
  const EarlyNonSupportedMessage = (
    <>
      Please follow these steps to close your Early account(s) and change your
      subscription. <br /> <br />
      1. Close your Early account(s) <br />
      2. Reselect tier to downgrade
    </>
  )
  const PassionsNonSupportedMessage = (
    <>
      {getRemoteString({ key: 'passions.downgrade.not_supported.header' })}{' '}
      <br /> <br />
      <StyledRemoteString
        stringKey={'passions.downgrade.not_supported.step_1'}
        wrappers={[
          {
            component: styled.a``,
            props: { href: 'mailto:support@acorns.com' },
          },
        ]}
      />{' '}
      <br />
      {getRemoteString({ key: 'passions.downgrade.not_supported.step_2' })}
    </>
  )
  const handlePassionsNonSupportedModalClose = () => {
    const cta = getRemoteString({
      key: 'passions.downgrade.not_supported.cta',
    })
    analytics.track('Button Tapped', PassionsModalConfirmCTA(cta))
    setShouldShowPassionsNotSupportedModal(false)
  }

  // primary and secondary actions for Cancel Confirmation Modal
  const handleContinueCancel = () => {
    handleStartCancelFlow()
  }
  const handleCloseConfirmationModal = () => {
    handleCloseEducationModal()
  }

  const actionStripActions: ActionStripAction[] = [
    {
      id: 1,
      text: `${isInvestOnly ? 'Cancel subscription' : 'Close account'}`,
      handler: handleActionClick,
      actionText: `${isInvestOnly ? 'Cancel subscription' : 'Close account'}`,
    },
  ]

  // when user tries to Cancel Subscription, they will get a modal that they need a verified funding source
  // in order to transfer money out of the Acorns account
  const getNonVerifiedFSModalProps = () => {
    const modalActions = [
      {
        handler: handleCloseFSVerificationModal,
        label: 'Back',
        type: ModalActionTypes.secondary,
      },
      {
        handler: handleSubmitVerifyFSModal,
        label: 'Verify now',
        type: ModalActionTypes.primary,
      },
    ]
    const modalHeader = 'Account verification needed'
    const modalBody =
      "Before you can close your account, you'll need to verify your primary checking account to transfer money out of Acorns"
    return {
      modalActions,
      modalHeader,
      modalBody,
    }
  }

  const getEducationModalProps = () => {
    const modalActions = [
      {
        handler: handleCloseEducationModal,
        label: 'Cancel',
        type: ModalActionTypes.secondary,
      },
      {
        handler: handleStartCancelFlow,
        label: 'Yes, Continue',
        type: ModalActionTypes.danger,
      },
    ]
    const modalHeader = 'Are you sure?'
    const modalBody =
      "You can pause your account activity from the 'Linked Accounts' page. If you still want to close your account we will sell your investments and the balance will be transferred back to your Funding source in 3-6 days."

    return {
      modalActions,
      modalHeader,
      modalBody,
    }
  }

  const getConfirmDowngradeModalProps = () => {
    const modalActions = [
      {
        handler: handleCancelDowngrade,
        label: 'Never mind',
        type: ModalActionTypes.secondary,
      },
      {
        handler: handleDowngradeSubscriptionPress,
        label: 'Change',
        type: ModalActionTypes.primary,
      },
    ]
    const modalHeader = 'Change your subscription?'
    const modalBody =
      selectedTierForChange?.key === TierKey.Bronze
        ? 'Switching to the Lite plan will change your subscription to $1 a month.'
        : 'Switching to the Personal plan will change your subscription to $3 a month.'
    return {
      modalActions,
      modalHeader,
      modalBody,
    }
  }

  const getConfirmUpgradeModalProps = () => {
    const modalActions = [
      {
        handler: handleCancelUpgrade,
        label: 'Never mind',
        type: ModalActionTypes.secondary,
      },
      {
        handler: handleUpgradeSubscriptionPress,
        label: 'Upgrade',
        type: ModalActionTypes.primary,
      },
    ]
    const modalHeader = 'Confirm your upgrade'
    const modalBody =
      selectedTierForChange?.key === TierKey.Silver
        ? 'Get all-in-one investment, retirement, and checking accounts — just $3 per month. No surprise fees.'
        : 'Get unlimited investment accounts for kids! Plus personal investment, retirement, and checking accounts, bonus investments, and more - just $5 per month. No hidden fees.'

    return {
      modalActions,
      modalHeader,
      modalBody,
    }
  }

  const getSpendUnavailableModalProps = () => {
    const modalActions = [
      {
        handler: handleCancelUpgrade,
        label: 'Never mind',
        type: ModalActionTypes.secondary,
      },
      {
        handler: handleUpgradeSubscriptionPress,
        label: 'Upgrade',
        type: ModalActionTypes.primary,
      },
    ]
    const modalHeader = 'Spend is unavailable'
    const modalBody =
      selectedTierForChange?.key === TierKey.Silver
        ? "We are unable to re-open your closed Spend account, but you'll still be able to open a Later account. Would you like to continue?"
        : "We are unable to re-open your closed Spend account, but you'll still be able to open a Later and Early account. Would you like to continue?"

    return {
      modalActions,
      modalHeader,
      modalBody,
    }
  }

  const nonVerifiedFSModalProps = getNonVerifiedFSModalProps()
  const educationConfirmationModalProps = getEducationModalProps()
  const upgradeModalProps = getConfirmUpgradeModalProps()
  const downgradeModalProps = getConfirmDowngradeModalProps()
  const spendUnavailableModalProps = getSpendUnavailableModalProps()

  useScrollIntoView(currentTierRef, currentTierShowing)

  useEffect(() => {
    if (accountTryingToCancel && startCancelFlow) {
      handleStartCancelFlow()
      setStartCancelFlow(false)
    }
  }, [accountTryingToCancel, startCancelFlow])

  return (
    <>
      {showEdgeCaseView && (
        <EdgeCasePage
          isWebView={isWebView}
          handleBackPress={handleBackPress}
          eligibilityStatus={
            edgeCaseErrorData as Omit<
              MergedProps['edgeCaseErrorData'],
              | 'spendRecurringTransferOn'
              | 'spendTransferId'
              | 'laterRecurringInvestment'
              | 'investRecurringInvestmentOn'
            >
          }
          showRecurringTransferModal={showRecurringTransferModal}
          handleRecurringTransferCancellation={
            handleRecurringTransferCancellation
          }
          setShowRecurringTransferModal={setShowRecurringTransferModal}
          isDowngradeFlow={selectedTierForChange?.isDowngrade}
          setShowEdgeCaseView={setShowEdgeCaseView}
        />
      )}
      {!showEdgeCaseView && (
        <StyledContainer>
          {isWebView ? (
            <StyledFullscreenTakeover innerHeight={height}>
              <WebViewActionStrip
                title="My Subscription"
                action={{
                  onClick: exitWebViewCancel,
                  shouldShowCloseIcon: true,
                }}
                link={{
                  text: 'History',
                  onClick: handleWVNavigateHistory,
                }}
              />

              {loading ? (
                <Loader>
                  <Spinner />
                </Loader>
              ) : (
                <ThemeProvider theme={customTheme}>
                  <TierContainer
                    onClick={handleClickOutside}
                    isWebView={isWebView}
                  >
                    {tierCardData &&
                      tierCardData.map((item, index) => {
                        const isCurrentPlan =
                          currentTier?.key === item?.tier?.key
                        const ref = isCurrentPlan ? currentTierRef : undefined
                        const isDowngrade =
                          (currentTier?.includedProducts?.length ?? 0) >
                          (item?.tier?.includedProducts?.length ?? 0)
                        return (
                          <div
                            key={`${currentTier?.key || item?.tier?.key}_tier`}
                          >
                            <StyledCol
                              xs={12}
                              sm={12}
                              md={12}
                              key={`column-${index}`}
                            >
                              {' '}
                              <TierCard
                                setCurrentTierShowing={setCurrentTierShowing}
                                ref={ref}
                                tier={item?.tier}
                                cost={item?.preferredTierPrice?.amount?.value.toString()}
                                currentPlan={isCurrentPlan}
                                handleChangeTierSelection={
                                  isDowngrade
                                    ? handleDowngradeTierSelection
                                    : handleUpgradeTierSelection
                                }
                                tierId={item?.preferredTierPrice?.id}
                                nextBillingDate={nextBillingDate}
                                currentTierInfo={currentTier}
                                isDowngradableTier={isDowngrade}
                              />
                            </StyledCol>
                          </div>
                        )
                      })}
                    <StyledCancelButtonWV onPress={handleActionClick} secondary>
                      {isInvestOnly ? 'Cancel subscription' : 'Close account'}
                    </StyledCancelButtonWV>
                    {showCloseAccountsDropdown && (
                      <>
                        <MediaQuery maxWidth={themeValues.breakpoints.sm - 1}>
                          <StyledOverlay onClick={handleClickOutside} />

                          <StyledMenu onClick={handleClickOutside}>
                            {accountsMenu &&
                              accountsMenu.map(({ product, icon, option }) => (
                                <Item
                                  key={product}
                                  onClick={() =>
                                    handleAccountsMenuItemPress(product)
                                  }
                                >
                                  <StyledIcon>{icon}</StyledIcon>
                                  <StyledText>{option}</StyledText>
                                  <AcornsIcon
                                    icon={AcornsIcon.Icon.ChevronRight}
                                    width="6px"
                                    height="10px"
                                  />
                                  <Ink opacity={0.02} />
                                </Item>
                              ))}
                          </StyledMenu>
                        </MediaQuery>
                      </>
                    )}
                  </TierContainer>
                </ThemeProvider>
              )}
            </StyledFullscreenTakeover>
          ) : (
            <>
              <ActionStripWrapper>
                <ActionStrip
                  maxWidth={1200}
                  title="Change tier"
                  actionText="Go back to Settings"
                  backOverride="/settings/my-subscription"
                  actions={!showEdgeCaseView ? actionStripActions : undefined}
                >
                  {showCloseAccountsDropdown && (
                    <MediaQuery minWidth={themeValues.breakpoints.md}>
                      <StyledMenu>
                        {accountsMenu &&
                          accountsMenu.map(({ product, icon, option }) => (
                            <Item
                              key={product}
                              onClick={() =>
                                handleAccountsMenuItemPress(product)
                              }
                            >
                              <StyledIcon>{icon}</StyledIcon>
                              <StyledText>{option}</StyledText>
                              <Ink opacity={0.02} />
                            </Item>
                          ))}
                      </StyledMenu>
                    </MediaQuery>
                  )}
                </ActionStrip>
              </ActionStripWrapper>
              {loading ? (
                <Loader>
                  <Spinner />
                </Loader>
              ) : (
                <>
                  <ThemeProvider theme={customTheme}>
                    <TierContainer
                      onClick={handleClickOutside}
                      isWebView={isWebView}
                    >
                      <MediaQuery minWidth={themeValues.breakpoints.sm}>
                        <>
                          {!loading &&
                            tierCardData &&
                            tierCardData.map((item, index) => {
                              const isDowngrade =
                                (currentTier?.includedProducts?.length ?? 0) >
                                (item?.tier?.includedProducts?.length ?? 0)

                              return (
                                // Using explicit declaration to pass key, see: https://reactjs.org/docs/fragments.html
                                <React.Fragment key={index}>
                                  {item.preferredTierPrice && (
                                    <StyledCol
                                      xs={12}
                                      sm={12}
                                      md={
                                        (tierCardData?.length ?? 0 <= 2) ? 6 : 4
                                      }
                                      lg={
                                        (tierCardData?.length ?? 0 <= 2) ? 6 : 4
                                      }
                                      key={`column-${index}`}
                                    >
                                      <TierCard
                                        setCurrentTierShowing={
                                          setCurrentTierShowing
                                        }
                                        tier={item?.tier}
                                        cost={item?.preferredTierPrice?.amount?.value.toString()}
                                        currentPlan={
                                          currentTier?.key === item?.tier?.key
                                        }
                                        handleChangeTierSelection={
                                          isDowngrade
                                            ? handleDowngradeTierSelection
                                            : handleUpgradeTierSelection
                                        }
                                        tierId={item?.preferredTierPrice?.id}
                                        nextBillingDate={nextBillingDate}
                                        currentTierInfo={currentTier}
                                        isDowngradableTier={isDowngrade}
                                      />
                                    </StyledCol>
                                  )}
                                </React.Fragment>
                              )
                            })}
                        </>
                      </MediaQuery>
                      <MediaQuery maxWidth={themeValues.breakpoints.sm - 1}>
                        <>
                          {tierCardData &&
                            !loading &&
                            tierCardData.map((item, index) => {
                              const isCurrentPlan =
                                currentTier?.key === item?.tier?.key
                              const ref = isCurrentPlan
                                ? currentTierRef
                                : undefined
                              const isDowngrade =
                                (currentTier?.includedProducts?.length ?? 0) >
                                (item?.tier?.includedProducts?.length ?? 0)
                              return (
                                <div
                                  key={`${
                                    currentTier?.key || item?.tier?.key
                                  }_tier_${index}`}
                                >
                                  <StyledCol
                                    xs={12}
                                    sm={12}
                                    key={`column-${index}`}
                                  >
                                    {' '}
                                    <TierCard
                                      setCurrentTierShowing={
                                        setCurrentTierShowing
                                      }
                                      ref={ref}
                                      tier={item?.tier}
                                      cost={item?.preferredTierPrice?.amount?.value.toString()}
                                      currentPlan={isCurrentPlan}
                                      handleChangeTierSelection={
                                        isDowngrade
                                          ? handleDowngradeTierSelection
                                          : handleUpgradeTierSelection
                                      }
                                      tierId={item?.preferredTierPrice?.id}
                                      nextBillingDate={nextBillingDate}
                                      currentTierInfo={currentTier}
                                      isDowngradableTier={isDowngrade}
                                    />
                                  </StyledCol>
                                </div>
                              )
                            })}
                        </>
                      </MediaQuery>

                      <MediaQuery maxWidth={themeValues.breakpoints.sm - 1}>
                        <StyledCancelButton
                          onPress={handleActionClick}
                          secondary
                        >
                          {isInvestOnly
                            ? 'Cancel subscription'
                            : 'Close account'}
                        </StyledCancelButton>
                      </MediaQuery>
                      <MediaQuery maxWidth={themeValues.breakpoints.md - 1}>
                        <>
                          {showCloseAccountsDropdown && (
                            <>
                              <StyledOverlay onClick={handleClickOutside} />

                              <StyledMenu onClick={handleClickOutside}>
                                {accountsMenu &&
                                  accountsMenu.map(
                                    ({ product, icon, option }) => (
                                      <Item
                                        key={product}
                                        onClick={() =>
                                          handleAccountsMenuItemPress(product)
                                        }
                                      >
                                        <StyledIcon>{icon}</StyledIcon>
                                        <StyledText>{option}</StyledText>
                                        <AcornsIcon
                                          icon={AcornsIcon.Icon.ChevronRight}
                                          width="6px"
                                          height="10px"
                                        />
                                        <Ink opacity={0.02} />
                                      </Item>
                                    ),
                                  )}
                              </StyledMenu>
                            </>
                          )}
                        </>
                      </MediaQuery>
                    </TierContainer>
                  </ThemeProvider>
                </>
              )}
            </>
          )}

          <ConfirmationModal
            actions={[
              {
                handler: handleCloseConfirmationModal,
                label: 'Cancel',
                type: ModalActionTypes.secondary,
              },
              {
                handler: handleContinueCancel,
                label: 'Yes, Continue',
                type: ModalActionTypes.danger,
              },
            ]}
            message={educationConfirmationModalProps.modalBody}
            title={educationConfirmationModalProps.modalHeader}
            isOpen={showEducationModal}
            onRequestClose={handleCloseConfirmationModal}
            image={undefined}
          />

          {shouldShowConfirmUpgradeModal && (
            <ConfirmationModal
              image={
                <AcornsIcon
                  icon={AcornsIcon.Icon.AcornsLogoWithBackground}
                  width="104px"
                />
              }
              actions={upgradeModalProps?.modalActions}
              message={upgradeModalProps?.modalBody}
              title={upgradeModalProps?.modalHeader}
              isOpen={shouldShowConfirmUpgradeModal}
              onRequestClose={() => setShouldShowConfirmUpgradeModal(false)}
            />
          )}
          {shouldShowNonVerifiedFSModal && (
            <ConfirmationModal
              actions={nonVerifiedFSModalProps.modalActions}
              message={nonVerifiedFSModalProps.modalBody}
              title={nonVerifiedFSModalProps.modalHeader}
              isOpen={shouldShowNonVerifiedFSModal}
              onRequestClose={() => setShouldShowNonVerifiedFSModal(false)}
              image={<AcornsIcon icon={AcornsIcon.Icon.Alert} width="104px" />}
            />
          )}

          {shouldShowLoadingSpinner && (
            <BaseModal isOpen={shouldShowLoadingSpinner} onRequestClose={noop}>
              <ProgressLoader
                animationTiming={animationTiming}
                progressAmount={progressAmount}
                icon={
                  <AcornsIcon
                    icon={AcornsIcon.Icon.AcornsLogo}
                    width="34px"
                    height="42px"
                  />
                }
              />
            </BaseModal>
          )}

          {shouldShowConfirmDowngradeModal && (
            <ConfirmationModal
              image={
                <AcornsIcon
                  icon={AcornsIcon.Icon.AcornsLogoWithBackground}
                  width="104px"
                />
              }
              actions={downgradeModalProps?.modalActions}
              message={downgradeModalProps?.modalBody}
              title={downgradeModalProps?.modalHeader}
              isOpen={shouldShowConfirmDowngradeModal}
              onRequestClose={() => setShouldShowConfirmDowngradeModal(false)}
            />
          )}

          {showSpendUnavailableModal && (
            <ConfirmationModal
              image={<AcornsIcon icon={AcornsIcon.Icon.Alert} width="104px" />}
              actions={spendUnavailableModalProps?.modalActions}
              message={spendUnavailableModalProps?.modalBody}
              title={spendUnavailableModalProps?.modalHeader}
              isOpen={showSpendUnavailableModal}
              onRequestClose={() => setShowSpendUnavailableModal(false)}
            />
          )}

          {shouldShowEarlyNotSupportedModal && (
            <ConfirmationModal
              image={<AcornsIcon icon={AcornsIcon.Icon.Alert} width="104px" />}
              title="Action required"
              message={EarlyNonSupportedMessage}
              isOpen={shouldShowEarlyNotSupportedModal}
              actions={[
                {
                  handler: handleEarlyModalCancel,
                  label: 'Never mind',
                  type: ModalActionTypes.secondary,
                },
                {
                  handler: routeToEarly,
                  label: 'Close Early',
                  type: ModalActionTypes.primary,
                },
              ]}
              onRequestClose={() => setShouldShowEarlyNotSupportedModal(false)}
            />
          )}

          {shouldShowPassionsNotSupportedModal && (
            <ConfirmationModal
              image={<AcornsIcon icon={AcornsIcon.Icon.Alert} width="104px" />}
              title={getRemoteString({
                key: 'passions.downgrade.not_supported.title',
              })}
              message={PassionsNonSupportedMessage}
              isOpen={shouldShowPassionsNotSupportedModal}
              actions={[
                {
                  handler: handlePassionsNonSupportedModalClose,
                  label: getRemoteString({
                    key: 'passions.downgrade.not_supported.cta',
                  }),
                  type: ModalActionTypes.primary,
                },
              ]}
              onRequestClose={handlePassionsNonSupportedModalClose}
              onOpenComplete={() =>
                analytics.track('Container Viewed', PassionsModalViewed)
              }
            />
          )}
        </StyledContainer>
      )}
    </>
  )
}

export default TieredRegSubscriptionCenter
